import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {FiChevronDown, FiChevronRight} from "react-icons/fi";
import {groupKey, useLazyGroups} from "../../../../hooks/useLazyGroups";
import styles from "./SubjectGroupedAnswers.module.css";

/**
 * Renders an exam attempt's answers as collapsed SUBJECT → TOPIC groups, each header carrying that
 * group's score, and fetches a group's answers only when it is opened.
 *
 * Building an answer card is expensive — the backend does a snapshot lookup per answer and signs an
 * attachment URL and a Cloudflare playback URL per question — so a long exam took a while to open
 * even though the reader only ever wants a topic or two. Headers alone tell you where the student
 * struggled ("4 / 7 in Cəbr"); the questions arrive when you ask for them.
 *
 * The per-question markup is supplied by the caller via `renderQuestion(item, number)`, so each page
 * keeps its own question/answer layout.
 *
 * @param groups      [{subjectId, subjectName, topicId, topicName, questionCount, correctCount}]
 * @param refreshKey  bump to reload the groups that are currently open
 * @param loadGroup   (group) => Promise<answer[]>
 */
const SubjectGroupedAnswers = ({groups, loadGroup, renderQuestion, refreshKey = 0}) => {
    const {t} = useTranslation();
    const list = Array.isArray(groups) ? groups : [];
    const {expanded, items, loading, errors, toggle, refresh} = useLazyGroups(loadGroup);

    // The caller bumps refreshKey after re-grading an answer; reload whatever is currently open so
    // the visible cards and their group scores agree with the new grade.
    useEffect(() => {
        if (refreshKey) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    // Nest the flat group list under one header per subject, preserving the given order.
    const subjects = [];
    const indexByKey = new Map();
    list.forEach((group) => {
        const key = group.subjectId != null ? `s${group.subjectId}` : group.subjectName || "__none__";
        if (!indexByKey.has(key)) {
            indexByKey.set(key, subjects.length);
            subjects.push({key, subjectName: group.subjectName, topics: []});
        }
        subjects[indexByKey.get(key)].topics.push(group);
    });

    const renderScore = (correct, total) => {
        const rate = total > 0 ? Math.round((correct * 100) / total) : 0;
        return (
            <span className={`${styles.score} ${rate < 50 ? styles.scoreLow : styles.scoreOk}`}>
                {t("pages.examAttemptDetail.correctCount", {correct, total})}
            </span>
        );
    };

    if (list.length === 0) return null;

    // Question numbering runs across the whole attempt, so a question keeps the same number no matter
    // which groups happen to be open. Built from the header counts, which are known upfront.
    let numberCursor = 0;
    const startNumbers = new Map();
    list.forEach((group) => {
        startNumbers.set(groupKey(group), numberCursor);
        numberCursor += Number(group.questionCount) || 0;
    });

    return (
        <>
            {subjects.map((subject) => {
                const subjectCorrect = subject.topics.reduce((s, g) => s + (Number(g.correctCount) || 0), 0);
                const subjectTotal = subject.topics.reduce((s, g) => s + (Number(g.questionCount) || 0), 0);
                return (
                    <div key={subject.key} className={styles.group}>
                        <div className={styles.subjectHeader}>
                            <span>{subject.subjectName || t("pages.examAttemptDetail.otherSubject")}</span>
                            {renderScore(subjectCorrect, subjectTotal)}
                        </div>

                        {subject.topics.map((topic) => {
                            const key = groupKey(topic);
                            const isOpen = !!expanded[key];
                            const answers = items[key];
                            const startAt = startNumbers.get(key) || 0;
                            return (
                                <div key={key} className={styles.topicGroup}>
                                    <button
                                        type="button"
                                        className={styles.topicHeader}
                                        onClick={() => toggle(topic)}
                                        aria-expanded={isOpen}
                                    >
                                        <span className={styles.topicHeaderLabel}>
                                            <span className={styles.chevron}>
                                                {isOpen ? <FiChevronDown size={14}/> : <FiChevronRight size={14}/>}
                                            </span>
                                            {topic.topicName || t("pages.examAttemptDetail.otherTopic")}
                                        </span>
                                        {renderScore(Number(topic.correctCount) || 0, Number(topic.questionCount) || 0)}
                                    </button>

                                    {isOpen && loading[key] && (
                                        <div className={styles.groupNote}>{t("common.loading")}</div>
                                    )}
                                    {isOpen && errors[key] && (
                                        <div className={styles.groupError}>{t("pages.examAttemptDetail.groupLoadError")}</div>
                                    )}
                                    {isOpen && answers && answers.map((item, i) => renderQuestion(item, startAt + i + 1))}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </>
    );
};

export default SubjectGroupedAnswers;
